export interface TokenCategory {
  name: string;
  description: string;
  tokens: {
    name: string;
    value: string;
    usage: string;
    hexOrCss?: string;
  }[];
}

export const DESIGN_SYSTEM_TOKENS: TokenCategory[] = [
  {
    name: 'Color Tokens (Light Mode Only)',
    description: 'Strict Light Mode color system designed around neutrality, ultra-high legibility, and no dark variations.',
    tokens: [
      { name: 'Primary (Brand Accent)', value: '#1A1A1A', usage: 'Primary buttons, active states, key brand markers', hexOrCss: '#1A1A1A' },
      { name: 'Primary Hover', value: '#000000', usage: 'Hover state for primary interactive elements', hexOrCss: '#000000' },
      { name: 'Primary Active', value: '#262626', usage: 'Pressed state for buttons and tabs', hexOrCss: '#262626' },
      { name: 'Primary Soft', value: '#F3F4F6', usage: 'Subtle container backgrounds, pills, active nav items', hexOrCss: '#F3F4F6' },
      { name: 'Background Canvas', value: '#FBFBFC', usage: 'Global application background (Warm neutral <5% saturation)', hexOrCss: '#FBFBFC' },
      { name: 'Background Subtle', value: '#F4F5F7', usage: 'Secondary sections, command bar, code snippets', hexOrCss: '#F4F5F7' },
      { name: 'Surface (Cards / Panels)', value: '#FFFFFF', usage: 'Cards, elevated modals, result sheets', hexOrCss: '#FFFFFF' },
      { name: 'Surface Elevated', value: '#FFFFFF', usage: 'Dropdowns, mega menus, tooltips, dialogs', hexOrCss: '#FFFFFF' },
      { name: 'Border (Subtle)', value: '#E5E7EB', usage: 'Hairline card dividers, input borders, nav bottom line', hexOrCss: '#E5E7EB' },
      { name: 'Border Strong', value: '#D1D5DB', usage: 'Focused inputs, hovered cards, table borders', hexOrCss: '#D1D5DB' },
      { name: 'Text Primary', value: '#1A1A1A', usage: 'Headings, primary results, active labels (WCAG AAA)', hexOrCss: '#1A1A1A' },
      { name: 'Text Secondary', value: '#4B5563', usage: 'Supporting copy, tool descriptions, breakdown values', hexOrCss: '#4B5563' },
      { name: 'Text Muted', value: '#9CA3AF', usage: 'Timestamps, keyboard hints, disabled labels', hexOrCss: '#9CA3AF' },
      { name: 'Success (Green)', value: '#059669', usage: 'Positive FX yield, savings indicator, verified status', hexOrCss: '#059669' },
      { name: 'Warning (Amber)', value: '#D97706', usage: 'High inflation warning, early payoff penalty alert', hexOrCss: '#D97706' },
      { name: 'Error (Rose)', value: '#DC2626', usage: 'Negative return, validation errors, field warnings', hexOrCss: '#DC2626' },
      { name: 'Info / Trust', value: '#2563EB', usage: 'Methodology links, verified institutional sources', hexOrCss: '#2563EB' },
    ],
  },
  {
    name: 'Typography Scale & Numeric Hierarchy',
    description: 'Tabular numeric formatting and clear hierarchical typographic steps for financial accuracy.',
    tokens: [
      { name: 'Display XL', value: '48px / 1.1 / Bold', usage: 'Homepage master headline, primary statement' },
      { name: 'Display L', value: '36px / 1.2 / Bold', usage: 'Section title, feature hero' },
      { name: 'Numeric XL (Results)', value: '36px - 44px / Tabular Numbers / Black', usage: 'Primary financial output (e.g., $4,862 / mo)' },
      { name: 'Numeric Large', value: '24px / Tabular Numbers / Bold', usage: 'Secondary metrics, total interest, savings' },
      { name: 'Numeric Medium', value: '16px / Tabular Numbers / SemiBold', usage: 'Table columns, breakdown rows, input values' },
      { name: 'H1', value: '30px / 1.25 / Bold', usage: 'Tool page primary header' },
      { name: 'H2', value: '22px / 1.3 / Bold', usage: 'Module headers, result card headings' },
      { name: 'H3', value: '18px / 1.4 / SemiBold', usage: 'Subsection headers, card titles' },
      { name: 'H4', value: '15px / 1.4 / SemiBold', usage: 'Input labels, list item titles' },
      { name: 'Body Large', value: '16px / 1.6 / Normal', usage: 'Lead paragraphs, introductory statements' },
      { name: 'Body Medium', value: '14px / 1.5 / Normal', usage: 'Standard interface text, descriptions' },
      { name: 'Body Small / Caption', value: '12px / 1.4 / Medium', usage: 'Timestamps, methodology disclosures, badges' },
      { name: 'Kbd / Mono Code', value: '10px - 11px / Monospace / Bold', usage: 'Keyboard shortcuts, currency ISO tickers' },
    ],
  },
  {
    name: 'Spacing System (8pt Grid)',
    description: 'Systematic proportional spacing rhythm ensuring mathematical layout balance.',
    tokens: [
      { name: 'space-1 (4px)', value: '0.25rem (4px)', usage: 'Tight badge padding, icon text gaps' },
      { name: 'space-2 (8px)', value: '0.5rem (8px)', usage: 'Button vertical padding, compact gaps' },
      { name: 'space-3 (12px)', value: '0.75rem (12px)', usage: 'Standard input vertical padding, chip spacing' },
      { name: 'space-4 (16px)', value: '1.0rem (16px)', usage: 'Button horizontal padding, card inner padding' },
      { name: 'space-5 (20px)', value: '1.25rem (20px)', usage: 'Intermediate container gaps' },
      { name: 'space-6 (24px)', value: '1.5rem (24px)', usage: 'Card padding, grid column gaps' },
      { name: 'space-8 (32px)', value: '2.0rem (32px)', usage: 'Section padding, hero margins' },
      { name: 'space-12 (48px)', value: '3.0rem (48px)', usage: 'Major homepage section dividers' },
      { name: 'space-16 (64px)', value: '4.0rem (64px)', usage: 'Hero vertical padding' },
    ],
  },
  {
    name: 'Border Radius & Elevation (Soft Minimal)',
    description: 'Controlled curvature avoiding excessive bubble geometry.',
    tokens: [
      { name: 'radius-sm', value: '6px', usage: 'Kbd tags, micro badges' },
      { name: 'radius-md', value: '10px', usage: 'Buttons, inputs, dropdown items' },
      { name: 'radius-lg', value: '16px', usage: 'Financial tool cards, result sheets, dialog panels' },
      { name: 'radius-full', value: '9999px', usage: 'Pill tags, live status indicators, avatar circles' },
      { name: 'shadow-xs', value: '0 1px 2px 0 rgba(0, 0, 0, 0.04)', usage: 'Standard resting card elevation' },
      { name: 'shadow-sm', value: '0 2px 4px 0 rgba(0, 0, 0, 0.06)', usage: 'Hovered tool cards, dropdown popovers' },
      { name: 'shadow-xl', value: '0 20px 25px -5px rgba(0, 0, 0, 0.1)', usage: 'Search Command Palette, Methodology Drawer' },
    ],
  },
];
